import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { ANIMATED_BACKGROUNDS } from './settingsConstants';

const { FiCheck, FiSlash } = FiIcons;

function BgPreview({ bgId, accent, isDark }) {
  const colors = isDark
    ? {
        constellation: '#0A0A0A',
        matrix: '#050B07',
        aurora: '#050510',
        fireflies: '#0A0F0A',
        ripple: '#060A12',
        cosmic: '#030308',
        blob: '#080810',
      }
    : {
        constellation: '#F0F4F8',
        matrix: '#E8F5E9',
        aurora: '#E8F0FF',
        fireflies: '#F0F8F0',
        ripple: '#DDEEFF',
        cosmic: '#F5F0FF',
        blob: '#F8F4FF',
      };

  return (
    <div
      className="w-full h-16 rounded-xl mb-3 flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: colors[bgId] || '#111111' }}
    >
      <div className="flex gap-1 items-end">
        {[0.4, 0.7, 1, 0.7, 0.4].map((h, i) => (
          <div
            key={i}
            className="w-1 rounded-full"
            style={{
              height: `${h * 24}px`,
              backgroundColor: accent,
              opacity: isDark ? 0.7 : 0.5,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function SectionLabel({ children, mutedText }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest mb-3" style={{ color: mutedText }}>
      {children}
    </p>
  );
}

// Animation picker only (color handled by themeColors.pageBg in color tab / AI tokens)
export default function TabBackground({ settings, onApplyChange, theme, isDark }) {
  const { textColor, mutedText, cardBg, modalBorder } = theme;
  const accent = (settings.themeColors?.accent || '#C2FFE0').toUpperCase();
  const isNone = !settings.backgroundType || settings.backgroundType === 'none';

  return (
    <div className="space-y-6">
      <div>
        <SectionLabel mutedText={mutedText}>Choose Animation</SectionLabel>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {/* None */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={() =>
              onApplyChange({
                backgroundType: 'none',
                customBackground: '',
                backgroundGradient: '',
              })
            }
            className="relative p-3 rounded-2xl border-2 transition-all text-left"
            style={{
              borderColor: isNone ? accent : modalBorder,
              backgroundColor: isNone ? `${accent}12` : cardBg,
            }}
          >
            {isNone && (
              <div
                className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: accent }}
              >
                <SafeIcon icon={FiCheck} className="w-3 h-3" style={{ color: '#000000' }} />
              </div>
            )}

            <div
              className="w-full h-16 rounded-xl mb-3 flex items-center justify-center"
              style={{ backgroundColor: isDark ? '#111111' : '#E5E5E5' }}
            >
              <SafeIcon icon={FiSlash} className="w-6 h-6" style={{ color: mutedText }} />
            </div>

            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-xs font-semibold" style={{ color: textColor }}>
                None
              </span>
            </div>
            <p className="text-[10px]" style={{ color: mutedText }}>
              Solid background only
            </p>
          </motion.button>

          {ANIMATED_BACKGROUNDS.map((bg) => {
            const isSelected = settings.backgroundType === bg.id;

            return (
              <motion.button
                key={bg.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() =>
                  onApplyChange({
                    backgroundType: bg.id,
                    customBackground: '',
                    backgroundGradient: '',
                  })
                }
                className="relative p-3 rounded-2xl border-2 transition-all text-left"
                style={{
                  borderColor: isSelected ? accent : modalBorder,
                  backgroundColor: isSelected ? `${accent}12` : cardBg,
                }}
              >
                {isSelected && (
                  <div
                    className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: accent }}
                  >
                    <SafeIcon icon={FiCheck} className="w-3 h-3" style={{ color: '#000000' }} />
                  </div>
                )}

                <BgPreview bgId={bg.id} accent={accent} isDark={isDark} />

                <div className="flex items-center gap-1.5 mb-0.5">
                  <SafeIcon icon={bg.icon} className="w-3.5 h-3.5" style={{ color: accent }} />
                  <span className="text-xs font-semibold" style={{ color: textColor }}>
                    {bg.name}
                  </span>
                </div>

                <p className="text-[10px]" style={{ color: mutedText }}>
                  {bg.description}
                </p>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Neon Mesh Gradient Studio */}
      <div className="pt-4 border-t" style={{ borderColor: modalBorder }}>
        <SectionLabel mutedText={mutedText}>NEON MESH GRADIENT STUDIO</SectionLabel>
        <p className="text-xs mb-3" style={{ color: mutedText }}>
          Synthesize custom multi-node procedural neon gradients with custom ambient color points.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {[
            { id: 'cyber_abyss', name: 'Cyber Abyss', c1: '#050a14', c2: '#06202a', c3: '#10b981', c4: '#06b6d4' },
            { id: 'emerald_matrix', name: 'Emerald Matrix', c1: '#020f09', c2: '#052a1a', c3: '#10b981', c4: '#047857' },
            { id: 'quantum_nebula', name: 'Quantum Nebula', c1: '#070414', c2: '#1e0a3c', c3: '#8b5cf6', c4: '#ec4899' },
            { id: 'solar_flare', name: 'Solar Flare', c1: '#0d0408', c2: '#2a0b16', c3: '#f59e0b', c4: '#ef4444' },
            { id: 'deep_void', name: 'Deep Void', c1: '#030508', c2: '#0b1320', c3: '#1e293b', c4: '#0f172a' },
          ].map((preset) => (
            <button
              key={preset.id}
              onClick={() => {
                const meshStyle = `radial-gradient(at 0% 0%, ${preset.c1} 0px, transparent 55%), radial-gradient(at 100% 0%, ${preset.c2} 0px, transparent 55%), radial-gradient(at 0% 100%, ${preset.c3}33 0px, transparent 55%), radial-gradient(at 100% 100%, ${preset.c4}33 0px, transparent 55%)`;
                try {
                  localStorage.setItem('memark_custom_mesh', JSON.stringify(preset));
                } catch (_) {}
                onApplyChange({
                  backgroundType: 'none',
                  backgroundGradient: meshStyle,
                  customBackground: preset.c1,
                });
              }}
              className="p-2.5 rounded-xl border text-left flex items-center justify-between transition-all"
              style={{
                borderColor: modalBorder,
                backgroundColor: cardBg,
              }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded-full border border-white/20"
                  style={{
                    background: `linear-gradient(135deg, ${preset.c3}, ${preset.c4})`,
                  }}
                />
                <span className="text-xs font-semibold" style={{ color: textColor }}>
                  {preset.name}
                </span>
              </div>
              <span className="text-[10px]" style={{ color: accent }}>
                Apply
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}